/**
 * 测试结果展示组件
 */

import React, { useMemo } from 'react';
import type { TestCaseResult, PlcVariable, VariableTestResult } from '../../types/testTypes';

// ============================================
// 类型定义
// ============================================

export interface TestResultViewProps {
  /** 测试结果 */
  readonly result: TestCaseResult;
  /** 输出变量定义 */
  readonly outputs: readonly PlcVariable[];
  /** 所有变量定义（可选，用于更完整的类型显示） */
  readonly allVariables?: readonly PlcVariable[];
}

// ============================================
// 子组件：结果摘要
// ============================================

interface ResultSummaryProps {
  readonly passed: boolean;
  readonly executionTime: number;
  readonly errorMessage?: string;
}

const ResultSummary: React.FC<ResultSummaryProps> = ({
  passed,
  executionTime,
  errorMessage
}) => {
  return (
    <div className={`plc-test-result-summary ${passed ? 'passed' : 'failed'}`}>
      <div className="plc-test-result-icon">
        <i className={`fa fa-${passed ? 'check-circle' : 'times-circle'}`} />
      </div>
      <div className="plc-test-result-info">
        <span className="plc-test-result-status">
          {passed ? '测试通过' : '测试失败'}
        </span>
        <span className="plc-test-result-time">
          执行耗时: {executionTime.toFixed(2)} ms
        </span>
      </div>
      {errorMessage && (
        <div className="plc-test-result-error">
          <i className="fa fa-exclamation-triangle" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
};

// ============================================
// 子组件：变量结果行
// ============================================

interface VariableResultRowProps {
  readonly result: VariableTestResult;
  readonly variable?: PlcVariable;
}

const VariableResultRow: React.FC<VariableResultRowProps> = ({
  result,
  variable
}) => {
    console.log('Rendering VariableResultRow', { result, variable });
    
  return (
    <tr className={`plc-test-result-row ${result.passed ? 'passed' : 'failed'}`}>
      <td className="plc-test-result-var">
        {result.variableName}
        {variable?.comment && (
          <span className="plc-test-var-comment">({variable.comment})</span>
        )}
      </td>
      <td className="plc-test-result-type">
        {variable?.dataType ?? 'BOOL'}
      </td>
      <td className="plc-test-result-expected">
        {result.expectedValue || <span className="plc-test-empty-value">未设置</span>}
      </td>
      <td className="plc-test-result-actual">
        {result.actualValue}
      </td>
      <td className="plc-test-result-status">
        {result.passed ? (
          <i className="fa fa-check plc-test-icon-passed" title="匹配" />
        ) : (
          <i className="fa fa-times plc-test-icon-failed" title="不匹配" />
        )}
      </td>
    </tr>
  );
};

// ============================================
// 主组件：测试结果视图
// ============================================

export const TestResultView: React.FC<TestResultViewProps> = ({
  result,
  outputs,
  allVariables
}) => {
  // 构建变量定义映射 - 优先使用 allVariables，否则使用 outputs
  const variableMap = useMemo(() => {
    const sourceVariables = allVariables ?? outputs;
    return new Map(sourceVariables.map(v => [v.name, v]));
  }, [outputs, allVariables]);
  
  return (
    <div className="plc-test-result">
      <div className="plc-test-section-header">
        <i className="fa fa-list-alt" />
        <span>测试结果</span>
      </div>
      
      <ResultSummary
        passed={result.passed}
        executionTime={result.executionTime}
        errorMessage={result.errorMessage}
      />
      
      {result.variableResults.length > 0 && (
        <div className="plc-test-result-table-wrapper">
          <table className="plc-test-table plc-test-result-table">
            <thead>
              <tr>
                <th>变量名</th>
                <th>类型</th>
                <th>期望值</th>
                <th>实际值</th>
                <th>结果</th>
              </tr>
            </thead>
            <tbody>
              {result.variableResults.map((varResult) => (
                <VariableResultRow
                  key={varResult.variableName}
                  result={varResult}
                  variable={variableMap.get(varResult.variableName)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TestResultView;